'use client';

import { useLayoutEffect, useRef, type HTMLAttributes } from 'react';
import { layoutKagemaneCards } from '@/lib/kagemane-media-layout';

/** Original DOM/key order stays stable across reflow, including playing/focused cards. */
export function KagemaneMasonry({ children, className = '', completeRowsOnly = false, columnCount, style, ...props }: HTMLAttributes<HTMLDivElement> & { completeRowsOnly?: boolean; columnCount?: number }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const fixedColumns = columnCount && Number.isFinite(columnCount) && columnCount > 0 ? Math.max(1, Math.floor(columnCount)) : undefined;

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const breakpoint = window.matchMedia('(min-width: 768px)');
    let cards = Array.from(root.children) as HTMLElement[];
    let frame = 0;
    let disposed = false;
    const reflow = () => {
      if (disposed || root.clientWidth <= 0) return;
      // Reconsider cards hidden by the previous container width before applying CSS visibility.
      for (const card of cards) card.style.display = '';
      // Hidden mobile preview cards must not reserve a column or vertical space.
      const visible = cards.filter((card) => window.getComputedStyle(card).display !== 'none');
      // Keep landscape cards at two columns on mobile too. Expanding them to
      // all four library columns strands empty space beside preceding portraits.
      const spans = visible.map(card => Number(card.dataset?.kagemaneColumnSpan) || 1);
      const { widths } = layoutKagemaneCards(root.clientWidth, visible.map(() => 0), breakpoint.matches, completeRowsOnly, columnCount, spans);
      visible.forEach((card, index) => {
        if (widths[index] !== undefined) card.style.width = `${widths[index]}px`;
      });
      // Measure at the final width: captions and late video metadata are part of the height.
      const layout = layoutKagemaneCards(root.clientWidth, visible.map((card) => card.getBoundingClientRect().height), breakpoint.matches, completeRowsOnly, columnCount, spans);
      visible.forEach((card, index) => {
        if (index >= layout.positions.length) {
          card.style.display = 'none';
          return;
        }
        const { left, top } = layout.positions[index];
        card.style.position = 'absolute';
        card.style.left = `${left}px`;
        card.style.top = `${top}px`;
      });
      root.style.display = 'block';
      root.style.height = `${layout.height}px`;
      root.dataset.masonryReady = 'true';
    };
    const schedule = () => {
      if (disposed || frame) return;
      frame = window.requestAnimationFrame(() => { frame = 0; reflow(); });
    };
    const observer = new ResizeObserver(schedule);
    observer.observe(root);
    cards.forEach((card) => observer.observe(card));
    // React creates a new children array on prompt/quote/state changes even when
    // the cards are unchanged. Track real DOM membership instead of rebuilding
    // all observers and synchronously measuring the wall on every parent render.
    const membership = new window.MutationObserver(() => {
      const next = Array.from(root.children) as HTMLElement[];
      const nextSet = new Set(next);
      const previousSet = new Set(cards);
      for (const card of cards) if (!nextSet.has(card)) observer.unobserve(card);
      for (const card of next) if (!previousSet.has(card)) observer.observe(card);
      cards = next;
      schedule();
    });
    membership.observe(root, { childList: true });
    breakpoint.addEventListener('change', schedule);
    reflow();
    return () => {
      disposed = true;
      observer.disconnect();
      membership.disconnect();
      breakpoint.removeEventListener('change', schedule);
      window.cancelAnimationFrame(frame);
    };
  }, [completeRowsOnly, columnCount]);

  return <div ref={rootRef} className={`relative grid grid-cols-2 items-start gap-4 md:grid-cols-[repeat(auto-fill,minmax(min(100%,240px),1fr))] ${className}`}
    style={fixedColumns ? { ...style, gridTemplateColumns: `repeat(${fixedColumns}, minmax(0, 1fr))` } : style} {...props}>{children}</div>;
}
